import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { formatDate, formatTime } from '../utils/dateFormatter';
import FulfillmentChart from './FulfillmentChart';

interface PendingOrder {
  id: number;
  name: string; // Order number (e.g. #1001)
  created_at: string;
  total_price: string;
  customer?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  line_items: Array<{
    title: string;
    quantity: number;
    sku: string;
  }>;
  tags: string;
}

interface FulfillmentStats {
  pendingOrders: {
    count: number;
    orders: PendingOrder[];
  };
  fulfilledHistory: Array<{
    date: string;
    fulfilledCount: number;
    incomingCount: number;
    pendingCount: number;
  }>;
  metrics: {
    averageLeadTimeHours: number;
    returnRate: number;
    totalRefunds: number;
    totalFulfilledInRange: number;
  };
  staffPerformance: Array<{
    id: number;
    count: number;
  }>;
  stagnantOrders: PendingOrder[];
  complexityStats: {
    simple: { count: number; avgTimeHours: number };
    medium: { count: number; avgTimeHours: number };
    complex: { count: number; avgTimeHours: number };
  };
}

type DateRangeOption = 'today' | '7days' | '14days' | '30days' | 'custom';

export default function OrderPreparation() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<FulfillmentStats | null>(null);
  
  const [rangeOption, setRangeOption] = useState<DateRangeOption>('7days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      let startDate: Date | undefined;
      let endDate: Date | undefined;
      const today = new Date();

      switch (rangeOption) {
        case 'today':
          startDate = new Date();
          break;
        case '7days':
          startDate = new Date();
          startDate.setDate(today.getDate() - 7);
          break;
        case '14days':
          startDate = new Date();
          startDate.setDate(today.getDate() - 14);
          break;
        case '30days':
          startDate = new Date();
          startDate.setDate(today.getDate() - 30);
          break;
        case 'custom':
          if (customStartDate) {
            startDate = new Date(customStartDate);
          }
          if (customEndDate) {
            endDate = new Date(customEndDate);
          }
          break;
      }

      const params: any = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await api.get('/shopify/fulfillment-stats', { params });
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching fulfillment stats:', err);
      showError(err.response?.data?.error || 'Error al obtener datos de Shopify');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [rangeOption]);

  const handleCustomSearch = () => {
    if (rangeOption === 'custom') {
      fetchStats();
    }
  };

  if (loading && !stats) {
    return (
      <div className="tab-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ marginBottom: '1rem' }}></div>
          <p>Cargando métricas de Shopify...</p>
        </div>
      </div>
    );
  }

  // Ordenar pedidos pendientes por fecha
  const sortedPendingOrders = stats ? [...stats.pendingOrders.orders].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  ) : [];

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2>Control de Preparación de Pedidos</h2>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select 
            className="input" 
            value={rangeOption} 
            onChange={(e) => setRangeOption(e.target.value as DateRangeOption)}
            style={{ minWidth: '150px' }}
          >
            <option value="today">Hoy</option>
            <option value="7days">Últimos 7 días</option>
            <option value="14days">Últimos 14 días</option>
            <option value="30days">Últimos 30 días</option>
            <option value="custom">Rango Personalizado</option>
          </select>

          {rangeOption === 'custom' && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input 
                type="date" 
                className="input" 
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
              <span>-</span>
              <input 
                type="date" 
                className="input" 
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
              <button className="btn btn-primary btn-small" onClick={handleCustomSearch}>
                Buscar
              </button>
            </div>
          )}

          <button className="btn btn-secondary" onClick={fetchStats} disabled={loading}>
            {loading ? '...' : '🔄 Actualizar'}
          </button>
        </div>
      </div>

      {!stats ? (
        <div className="error-state">
          No se pudieron cargar los datos.
          <button className="btn btn-primary" onClick={fetchStats} style={{ marginTop: '1rem' }}>
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {/* Stagnant Orders Alert */}
          {stats.stagnantOrders && stats.stagnantOrders.length > 0 && (
            <div style={{ 
              backgroundColor: '#fff3cd', 
              color: '#856404', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '2rem',
              border: '1px solid #ffeeba'
            }}>
              <strong>⚠️ Alerta:</strong> Hay {stats.stagnantOrders.length} pedidos sin enviar con más de 3 días de antigüedad.
            </div>
          )}

          {/* KPI Cards Row 1 (Counts) */}
          <div className="dashboard-grid" style={{ marginBottom: '2rem' }}>
            <div className="dashboard-card" style={{ borderLeft: '4px solid var(--warning)' }}>
              <h3>Pendientes</h3>
              <div className="kpi-value">{stats.pendingOrders.count}</div>
              <p className="kpi-subtitle">Total Actual</p>
            </div>

            <div className="dashboard-card" style={{ borderLeft: '4px solid var(--success)' }}>
              <h3>Preparados</h3>
              <div className="kpi-value">
                {stats.metrics.totalFulfilledInRange}
              </div>
              <p className="kpi-subtitle">En el periodo</p>
            </div>

            <div className="dashboard-card" style={{ borderLeft: '4px solid var(--accent)' }}>
              <h3>Entrantes</h3>
              <div className="kpi-value">
                {stats.fulfilledHistory.reduce((acc, curr) => acc + curr.incomingCount, 0)}
              </div>
              <p className="kpi-subtitle">En el periodo</p>
            </div>
          </div>

          {/* KPI Cards Row 2 (Metrics) */}
          <div className="dashboard-grid" style={{ marginBottom: '2rem' }}>
            <div className="dashboard-card">
              <h3>Tiempo Medio Preparación</h3>
              <div className="kpi-value">
                {stats.metrics.averageLeadTimeHours.toFixed(1)} h
              </div>
              <p className="kpi-subtitle">Desde creación hasta envío</p>
            </div>

            <div className="dashboard-card">
              <h3>Tasa de Devoluciones</h3>
              <div className="kpi-value" style={{ color: stats.metrics.returnRate > 5 ? 'var(--error)' : 'inherit' }}>
                {stats.metrics.returnRate.toFixed(1)}%
              </div>
              <p className="kpi-subtitle">{stats.metrics.totalRefunds} reembolsos en el periodo</p>
            </div>

            <div className="dashboard-card">
               <h3>Complejidad Media</h3>
               <div style={{ marginTop: '10px' }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                   <span>Simple (1 item):</span>
                   <strong>{stats.complexityStats.simple.avgTimeHours.toFixed(1)}h</strong>
                 </div>
                 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                   <span>Medio (2-5 items):</span>
                   <strong>{stats.complexityStats.medium.avgTimeHours.toFixed(1)}h</strong>
                 </div>
                 <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                   <span>Complejo (5+):</span>
                   <strong>{stats.complexityStats.complex.avgTimeHours.toFixed(1)}h</strong>
                 </div>
               </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
            {/* Chart */}
            <div className="dashboard-card" style={{ height: '400px' }}>
              <h3>Ritmo de Pedidos (Diario vs Acumulado)</h3>
              <FulfillmentChart data={stats.fulfilledHistory} />
            </div>

            {/* Staff Ranking */}
            <div className="dashboard-card">
              <h3>Ranking Operarios</h3>
              {stats.staffPerformance.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)' }}>No hay datos de operarios disponibles.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID Staff</th>
                      <th>Pedidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.staffPerformance.map((staff, idx) => (
                      <tr key={staff.id}>
                        <td>
                          {idx === 0 && '🥇 '}
                          {idx === 1 && '🥈 '}
                          {idx === 2 && '🥉 '}
                          Usuario #{staff.id}
                        </td>
                        <td><strong>{staff.count}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Stagnant Orders List (if any) */}
          {stats.stagnantOrders && stats.stagnantOrders.length > 0 && (
             <div className="table-container" style={{ marginBottom: '2rem', border: '2px solid var(--warning)' }}>
              <h3 style={{ color: '#856404' }}>⚠️ Pedidos Estancados (+3 días)</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Días Esperando</th>
                    <th>Cliente</th>
                    <th>Items</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.stagnantOrders.map((order) => {
                    const daysWaiting = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={order.id}>
                        <td><strong>{order.name}</strong></td>
                        <td style={{ color: 'var(--error)', fontWeight: 'bold' }}>{daysWaiting} días</td>
                        <td>{order.customer?.first_name} {order.customer?.last_name}</td>
                        <td>{order.line_items.length} items</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending Orders List */}
          <div className="table-container">
            <h3>Todos los Pedidos Pendientes</h3>
            {sortedPendingOrders.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                🎉 ¡Todo al día! No hay pedidos pendientes.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Items</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPendingOrders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <strong>{order.name}</strong>
                        {order.tags && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            {order.tags}
                          </div>
                        )}
                      </td>
                      <td>
                        {formatDate(order.created_at)}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {formatTime(order.created_at)}
                        </div>
                      </td>
                      <td>
                        {order.customer ? (
                          <>
                            {order.customer.first_name} {order.customer.last_name}
                          </>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>-</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {order.line_items.map((item, idx) => (
                            <div key={idx} style={{ fontSize: '0.875rem' }}>
                              <span style={{ fontWeight: 500 }}>{item.quantity}x</span> {item.title}
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{item.sku}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td>{order.total_price} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}